import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User, UserRole } from '../types';

type AuthUser = Pick<User, 'id' | 'name' | 'email' | 'role'>;

interface AuthContextValue {
  user: AuthUser | null;
  setUser: (user: AuthUser | User | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem('adminUser');
    if (!raw) return null;
    const u = JSON.parse(raw) as { id?: string; name?: string; email?: string; role?: UserRole };
    if (u && (u.name != null || u.email != null)) {
      return {
        id: u.id ?? '',
        name: u.name ?? '',
        email: u.email ?? '',
        role: u.role === 'admin' ? 'admin' : 'operator',
      };
    }
    return null;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<AuthUser | null>(() => readStoredUser());

  useEffect(() => {
    const stored = readStoredUser();
    setUserState(stored);
  }, []);

  const setUser = (u: AuthUser | User | null) => {
    const role: UserRole = u?.role === 'admin' ? 'admin' : 'operator';
    const displayUser: AuthUser | null = u
      ? { id: u.id ?? '', name: u.name ?? '', email: u.email ?? '', role }
      : null;
    setUserState(displayUser);
    if (u) {
      localStorage.setItem('adminUser', JSON.stringify({ ...u, role: displayUser?.role ?? 'operator' }));
    } else {
      localStorage.removeItem('adminUser');
    }
  };

  const logout = () => {
    setUserState(null);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('adminUser');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
